import { useState, useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useAccount, useIsAuthenticated } from "@azure/msal-react";
import { getDeviceType, generateDeviceId } from "../utils";
import { sendMfaOpt } from "repositories/user-repository";
import {
  persistMfaValidatedForAccount,
  readMfaValidatedRaw,
  clearMfaValidated,
  getStoredMfaHomeAccountId,
  MFA_STORAGE_CHANGED_EVENT,
  persistPendingMfaSendPayload,
  markMfaInitSendTimestamp,
  resolveMfaHistoryId,
  bindMfaValidatedHomeAccountId,
} from "utils/mfaStorage";
import { useAuth } from "hooks/useAuth";
import { handleWakePhaseAuthFailure } from "utils/handleWakePhaseAuthFailure";

/**
 * MFA (multi-factor authentication) flow against the backend, using MSAL account claims.
 *
 * @param {() => Promise<void>} [_getTokenManageState] - Unused; profile bootstrap is owned by useAppSharedState.
 * @param {boolean} tokenAcquired - Becomes true once acquireTokenSilent has stored
 *   `msal.idtoken` in localStorage. The MFA check is deferred until this flag is
 *   true to avoid calling SendEmailMfa with a null token (race condition on first
 *   render after a B2C redirect).
 * @param {boolean} [isAuthenticatedFromApp] - When set, used instead of
 *   `useIsAuthenticated()` so MFA matches {@link useMsalAppSession} (context accounts
 *   can lag after redirect).
 */
export const useMfa = (
  _getTokenManageState,
  tokenAcquired = false,
  isAuthenticatedFromApp = undefined,
) => {
  const account = useAccount();
  const isAuthenticatedFromHook = useIsAuthenticated();
  const isAuthenticated =
    typeof isAuthenticatedFromApp === "boolean"
      ? isAuthenticatedFromApp
      : isAuthenticatedFromHook;
  const [isMfaValidate, setIsMfaValidate] = useState(readMfaValidatedRaw);
  const [isMfaChecking, setIsMfaChecking] = useState(false);
  /**
   * After B2C, user lands on `/` with !isMfaValidate. We must run SendEmailMfa preflight
   * before App.js can Redirect to `/authenticate`; otherwise the boolean `data: true`
   * (trusted device / no MFA) response is never applied.
   */
  const [mfaHomePreflightDone, setMfaHomePreflightDone] = useState(() => {
    if (readMfaValidatedRaw()) return true;
    if (typeof window === "undefined") return false;
    const path = window.location.pathname;
    return path !== "/" && path !== "";
  });
  const history = useHistory();
  const { signinHandler } = useAuth();
  /** Avoid duplicate SendEmailMfa when deps churn (e.g. accountInfo identity). */
  const mfaPreflightDispatchedRef = useRef(false);
  const homeMfaDispatchedRef = useRef(false);

  const checkMfaStatus = useCallback(async () => {
    if (isMfaChecking) return;

    try {
      setIsMfaChecking(true);
      const token = localStorage.getItem("msal.idtoken");
      const deviceId = generateDeviceId();
      const deviceType = getDeviceType();

      const params = {
        deviceId,
        deviceType,
      };

      const res = await sendMfaOpt(params, token, false);
      if (res?.statusCode === 401 && res?.message === "User does not exists.") {
        persistMfaValidatedForAccount(account);
        setIsMfaValidate(true);
        // Profile bootstrap runs once via useAppSharedState when isMfaValidate flips.
        history.replace("/");
        return;
      }
      if (typeof res?.data === "boolean") {
        persistMfaValidatedForAccount(account);
        setIsMfaValidate(true);
        history.replace("/");
      } else {
        // Keep UserMfaHistoryId available if /authenticate mounts without router state.
        if (resolveMfaHistoryId(res?.data) != null) {
          persistPendingMfaSendPayload(res.data);
          markMfaInitSendTimestamp();
        }
        history.push({ pathname: "/authenticate", state: res });
      }
    } catch (error) {
      if (handleWakePhaseAuthFailure(error, signinHandler)) {
        return;
      }
      console.error("Error checking MFA status:", error);
      history.push("/authenticate");
    } finally {
      setIsMfaChecking(false);
      setMfaHomePreflightDone(true);
    }
  }, [isMfaChecking, history, account, signinHandler]);

  /**
   * MultifactorAuth calls `persistMfaValidatedForAccount` on OTP success but cannot call
   * `setIsMfaValidate` here. Sync from storage so `history.replace("/")` is not undone by
   * App.js redirecting back to `/authenticate` while `isMfaValidate` was still false.
   */
  useEffect(() => {
    const syncFromStorage = () => {
      setIsMfaValidate(readMfaValidatedRaw());
    };
    syncFromStorage();
    window.addEventListener(MFA_STORAGE_CHANGED_EVENT, syncFromStorage);
    return () => {
      window.removeEventListener(MFA_STORAGE_CHANGED_EVENT, syncFromStorage);
    };
  }, []);

  /** Drop stale MFA flag when a different B2C user signs in on the same browser. */
  useEffect(() => {
    if (!account?.homeAccountId || !readMfaValidatedRaw()) return;

    const storedHomeId = getStoredMfaHomeAccountId();
    if (!storedHomeId) {
      // Do not re-dispatch MFA_STORAGE_CHANGED — that remounts AllRoutes after login.
      bindMfaValidatedHomeAccountId(account);
      return;
    }
    if (storedHomeId !== account.homeAccountId) {
      clearMfaValidated();
      setIsMfaValidate(false);
    }
  }, [account]);

  useEffect(() => {
    if (!isAuthenticated) {
      homeMfaDispatchedRef.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Wait until acquireTokenSilent has stored the token so we never call
    // SendEmailMfa with a null bearer token (race condition after B2C redirect).
    if (!tokenAcquired) return;

    const path = window.location.pathname;
    const isRootPath = path === "/" || path === "";

    if (isMfaValidate) {
      setMfaHomePreflightDone(true);
      return;
    }

    if (!isRootPath) {
      setMfaHomePreflightDone(true);
      return;
    }

    if (!isAuthenticated || !account) {
      return;
    }

    const hasIdToken = localStorage.getItem("msal.idtoken");
    if (!hasIdToken) {
      setMfaHomePreflightDone(true);
      return;
    }

    const acr = account?.idTokenClaims?.acr;

    if (
      acr === "b2c_1a_trustframeworkbase_su" || acr === "b2c_1a_passwordreset"
    ) {
      persistMfaValidatedForAccount(account);
      setIsMfaValidate(true);
      mfaPreflightDispatchedRef.current = false;
      setMfaHomePreflightDone(true);
      return;
    }

    const needsMfaCheck =
      acr === "b2c_1a_trustframeworkbase_susi" && !isMfaValidate;

    if (needsMfaCheck && isRootPath) {
      if (!homeMfaDispatchedRef.current) {
        homeMfaDispatchedRef.current = true;
        void checkMfaStatus();
      }
      return;
    }

    setMfaHomePreflightDone(true);
  }, [account, isAuthenticated, isMfaValidate, checkMfaStatus, tokenAcquired]);

  return {
    isMfaValidate,
    isMfaChecking,
    checkMfaStatus,
    setIsMfaValidate,
    mfaHomePreflightDone,
  };
};
